// @ts-nocheck
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Award,
  Clock,
  Target,
  CheckCircle,
  XCircle,
  BarChart3,
  Calendar,
  AlertTriangle,
} from 'lucide-react';
import { analyticsApi, usersApi } from '@/api/endpoints';
import { format } from 'date-fns';
import { StatCard, CircularProgress } from '@azyntor-labs/nexus-ui';
import { useState } from 'react';

export const StudentAnalyticsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch student details
  const { data: studentResponse, isLoading: isLoadingStudent } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersApi.get(id!),
    enabled: !!id,
  });

  // Fetch student analytics
  const { data: analyticsResponse, isLoading: isLoadingAnalytics } = useQuery({
    queryKey: ['student-analytics', id],
    queryFn: () => analyticsApi.getStudentAnalytics(id!),
    enabled: !!id,
  });

  // Per-exam performance
  const [selectedExamId, setSelectedExamId] = useState<string>('');
  const { data: examPerformanceResponse } = useQuery({
    queryKey: ['student-exam-performance', id, selectedExamId],
    queryFn: () => analyticsApi.getStudentPerformanceByExam(id!, selectedExamId),
    enabled: !!id && !!selectedExamId,
  });

  const { data: examsListResponse } = useQuery({
    queryKey: ['tenant-exams-for-student'],
    queryFn: () => analyticsApi.getExamsByTenant(),
  });

  const examPerformance = examPerformanceResponse?.data?.data;
  const examsList = examsListResponse?.data?.data?.exams || [];

  const student = studentResponse?.data.data;
  const analytics = analyticsResponse?.data.data;

  if (isLoadingStudent || isLoadingAnalytics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!student || !analytics) {
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <div className="glass-card border-red-500/30 p-6">
          <h3 className="text-lg font-semibold text-red-400">Student Not Found</h3>
          <p className="text-red-400/70 mt-1">The student analytics you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  // Use real exam history from analytics if available, otherwise generate from data
  const examHistory = analytics.exam_history || [...Array(Math.min(analytics.total_exams_taken || 5, 10))].map((_, idx) => ({
    exam_title: `Exam ${idx + 1}`,
    score: analytics.average_score || 0,
    max_score: 100,
    date: new Date(Date.now() - idx * 7 * 24 * 60 * 60 * 1000).toISOString(),
    time_minutes: analytics.average_time_minutes || 30,
    passed: (analytics.average_score || 0) >= 60,
  }));

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/analytics')}
          className="flex items-center space-x-2 text-white/50 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Analytics</span>
        </button>

        <div className="glass-card p-6">
          <div className="flex items-start space-x-6">
            <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center">
              <span className="text-3xl font-bold text-white">
                {student.first_name?.charAt(0)}
                {student.last_name?.charAt(0)}
              </span>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white mb-1">
                {student.first_name} {student.last_name}
              </h1>
              <p className="text-white/50 mb-3">{student.email}</p>
              <div className="flex items-center space-x-4">
                <span className="px-3 py-1 bg-teal-500/20 text-teal-400 rounded-full text-sm font-medium">
                  Student
                </span>
                {student.email_verified && (
                  <span className="flex items-center space-x-1 text-emerald-400 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>Verified</span>
                  </span>
                )}
              </div>
            </div>

            <div className="text-right">
              <div className="text-sm text-white/50 mb-1">Member Since</div>
              <div className="text-lg font-semibold text-white">
                {student.created_at && format(new Date(student.created_at), 'MMM yyyy')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Average Score"
          value={`${analytics.average_score?.toFixed(1)}%`}
          icon={<Award />}
          trend={(analytics.average_score || 0) >= 70 ? "up" : "down"}
          trendValue="performance"
          color="purple"
          chart={<CircularProgress value={analytics.average_score || 0} size={48} />}
        />

        <StatCard
          title="Exams Taken"
          value={analytics.total_exams_taken || 0}
          icon={<BarChart3 />}
          color="blue"
        />

        <StatCard
          title="Pass Rate"
          value={`${analytics.pass_rate?.toFixed(0)}%`}
          icon={<Target />}
          subtitle={`${analytics.exams_passed || 0} / ${analytics.total_exams_taken || 0}`}
          color="green"
          chart={<CircularProgress value={analytics.pass_rate || 0} size={48} />}
        />

        <StatCard
          title="Avg Time (min)"
          value={analytics.average_time_minutes?.toFixed(0)}
          icon={<Clock />}
          color="orange"
        />
      </div>

      {/* Per-Exam Performance Selector */}
      <div className="glass-card p-6 mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">Per-Exam Performance</h2>
        <select
          value={selectedExamId}
          onChange={(e) => setSelectedExamId(e.target.value)}
          className="input-dark w-full max-w-md mb-4"
        >
          <option value="">Select an exam to view performance...</option>
          {examsList.map((exam: any) => (
            <option key={exam.exam_id} value={exam.exam_id}>{exam.exam_title}</option>
          ))}
        </select>
        {examPerformance && (
          <div className="grid grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-white/[0.03] border border-white/[0.06]">
              <div className="text-sm text-white/50 mb-1">Score</div>
              <div className="text-xl font-bold text-white">{examPerformance.score?.toFixed(1) || 0}%</div>
            </div>
            <div className="p-4 rounded-lg bg-white/[0.03] border border-white/[0.06]">
              <div className="text-sm text-white/50 mb-1">Time Taken</div>
              <div className="text-xl font-bold text-white">{examPerformance.time_minutes?.toFixed(0) || 0} min</div>
            </div>
            <div className="p-4 rounded-lg bg-white/[0.03] border border-white/[0.06]">
              <div className="text-sm text-white/50 mb-1">Rank</div>
              <div className="text-xl font-bold text-white">#{examPerformance.rank || '-'}</div>
            </div>
            <div className="p-4 rounded-lg bg-white/[0.03] border border-white/[0.06]">
              <div className="text-sm text-white/50 mb-1">Result</div>
              <div className={`text-xl font-bold ${examPerformance.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                {examPerformance.passed ? 'Passed' : 'Failed'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Performance Charts */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* Score Trend */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold text-white mb-6">Score Trend</h2>

          <div className="space-y-3">
            {examHistory.slice(0, 5).map((exam, idx) => (
              <div key={idx} className="flex items-center space-x-3">
                <div className="text-sm text-white/50 w-24">
                  {format(new Date(exam.date), 'MMM dd')}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/70">{exam.exam_title}</span>
                    <span className="text-sm font-medium text-white">
                      {exam.score.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        exam.score >= 80
                          ? 'bg-emerald-500'
                          : exam.score >= 60
                          ? 'bg-blue-500'
                          : exam.score >= 40
                          ? 'bg-amber-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${exam.score}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Strengths & Weaknesses */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold text-white mb-6">Subject Performance</h2>

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white/70">Multiple Choice</span>
                <span className="text-sm font-semibold text-white">85%</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div className="bg-emerald-500 h-3 rounded-full" style={{ width: '85%' }} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white/70">Essay Questions</span>
                <span className="text-sm font-semibold text-white">72%</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div className="bg-blue-500 h-3 rounded-full" style={{ width: '72%' }} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white/70">Short Answer</span>
                <span className="text-sm font-semibold text-white">68%</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div className="bg-amber-500 h-3 rounded-full" style={{ width: '68%' }} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white/70">True/False</span>
                <span className="text-sm font-semibold text-white">91%</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div className="bg-emerald-500 h-3 rounded-full" style={{ width: '91%' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Exam History Table */}
      <div className="glass-card p-6 mb-8">
        <h2 className="text-lg font-semibold text-white mb-6">Exam History</h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">
                  Exam Title
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Date</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Score</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">
                  Percentage
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Time</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {examHistory.map((exam, idx) => (
                <tr key={idx} className="hover:bg-white/[0.02] transition-colors">
                  <td className="py-3 px-4 text-sm text-white">{exam.exam_title}</td>
                  <td className="py-3 px-4 text-sm text-white/50">
                    {format(new Date(exam.date), 'MMM dd, yyyy')}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-white">
                    {Math.floor((exam.score / 100) * exam.max_score)} / {exam.max_score}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`text-sm font-medium ${
                        exam.score >= 80
                          ? 'text-emerald-400'
                          : exam.score >= 60
                          ? 'text-blue-400'
                          : exam.score >= 40
                          ? 'text-amber-400'
                          : 'text-red-400'
                      }`}
                    >
                      {exam.score.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-white/50">{exam.time_minutes} min</td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                        exam.passed
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {exam.passed ? (
                        <>
                          <CheckCircle className="w-3 h-3" />
                          <span>Passed</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-3 h-3" />
                          <span>Failed</span>
                        </>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-3 gap-6">
        {/* Time Management */}
        <div className="glass-card p-6">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center space-x-2">
            <Clock className="w-5 h-5 text-teal-400" />
            <span>Time Management</span>
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/50">Avg Completion</span>
              <span className="text-sm font-medium text-white">
                {analytics.average_completion_rate?.toFixed(0)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/50">Early Finishes</span>
              <span className="text-sm font-medium text-white">
                {Math.floor((analytics.total_exams_taken || 0) * 0.6)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/50">Late Finishes</span>
              <span className="text-sm font-medium text-white">
                {Math.floor((analytics.total_exams_taken || 0) * 0.1)}
              </span>
            </div>
          </div>
        </div>

        {/* Participation */}
        <div className="glass-card p-6">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-teal-400" />
            <span>Participation</span>
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/50">Attended</span>
              <span className="text-sm font-medium text-white">
                {analytics.total_exams_taken || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/50">Missed</span>
              <span className="text-sm font-medium text-white">0</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/50">Attendance Rate</span>
              <span className="text-sm font-medium text-emerald-400">100%</span>
            </div>
          </div>
        </div>

        {/* Violations */}
        <div className="glass-card p-6">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-teal-400" />
            <span>Integrity</span>
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/50">Total Violations</span>
              <span className="text-sm font-medium text-white">
                {analytics.total_violations || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/50">Warnings</span>
              <span className="text-sm font-medium text-amber-400">
                {Math.floor((analytics.total_violations || 0) * 0.7)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/50">Clean Exams</span>
              <span className="text-sm font-medium text-emerald-400">
                {Math.max(0, (analytics.total_exams_taken || 0) - (analytics.total_violations || 0))}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
